import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../utils/apiClient";
import { toast } from "react-toastify";
import { useState, useEffect, useRef } from "react";
import ContentLoader from "../../utils/ContentLoader";
import ReadingPlanForm from "../../components/forms/ReadingPlanForm";
import Breadcrumb from "../../components/Breadcrumb";

export default function ReadingPlanFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [initialData, setInitialData] = useState({});
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [usersLoading, setUsersLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(Boolean(id));
    const searchTimeout = useRef(null);

    useEffect(() => {
        fetchUsers("");
        if (id) fetchPlan();
    }, [id]);

    const fetchUsers = async (search) => {
        try {
            setUsersLoading(true);
            const res = await api.get("/mobile-users", {
                params: { limit: 50, search: search || "" },
            });
            const users = res.data?.data || [];
            const options = users.map((u) => ({
                label: `${u.name} (${u.email || ""})`,
                value: u.id,
            }));
            setUserOptions((prev) => {
                // Keep the currently selected user in the list so it doesn't disappear
                if (selectedUser && !options.find((o) => o.value === selectedUser.value)) {
                    return [selectedUser, ...options];
                }
                return options;
            });
        } catch {
            toast.error("Failed to load users");
        } finally {
            setUsersLoading(false);
        }
    };

    // Only search when user is actually typing, not on blur/value-set after selection
    const handleUserInputChange = (inputValue, { action }) => {
        if (action !== "input-change") return;
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            fetchUsers(inputValue);
        }, 400);
    };

    const fetchPlan = async () => {
        try {
            setPageLoading(true);
            const res = await api.get(`/readingplan/${id}`);
            if (!res.data.status) {
                toast.error(res.data.message || "Failed to fetch reading plan");
                navigate("/readingplan");
                return;
            }
            setInitialData(res.data.data);
        } catch {
            toast.error("Something went wrong");
            navigate("/readingplan");
        } finally {
            setPageLoading(false);
        }
    };

    const handleSubmit = async (formData) => {
        try {
            setLoading(true);
            if (id) {
                await api.post(`/readingplan/${id}`, formData);
                toast.success("Reading plan updated successfully");
            } else {
                await api.post("/readingplan", formData);
                toast.success("Reading plan created successfully");
            }
            navigate("/readingplan");
        } catch (err) {
            const errorMessage =
                Object.values(err?.response?.data?.errors || {})?.[0]?.[0] ||
                err?.response?.data?.message ||
                "Something went wrong";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    if (pageLoading) return <ContentLoader />;

    return (
        <>
            <Breadcrumb title={id ? "Edit Reading Plan" : "Create Reading Plan"} />
            <ReadingPlanForm
                onSubmit={handleSubmit}
                initialData={initialData}
                userOptions={userOptions}
                usersLoading={usersLoading}
                onUserInputChange={handleUserInputChange}
                onUserSelect={setSelectedUser}
                loading={loading}
            />
        </>
    );
}
